import React, { useEffect, useState, forwardRef } from "react";
import dayjs, { Dayjs } from "dayjs";
import {
  DateRangePicker,
  SingleInputDateRangeField,
} from "@mui/x-date-pickers-pro";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { InputAdornment, IconButton } from "@mui/material";
import type { SxProps, Theme } from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CloseIcon from "@mui/icons-material/Close";

const blockedInputProps = {
  readOnly: true,
  onKeyDown: (e: React.KeyboardEvent) => e.preventDefault(),
  onKeyPress: (e: React.KeyboardEvent) => e.preventDefault(),
  onKeyUp: (e: React.KeyboardEvent) => e.preventDefault(),
  onInput: (e: React.FormEvent) => e.preventDefault(),
  onBeforeInput: (e: any) => e.preventDefault(),
  onPaste: (e: React.ClipboardEvent) => e.preventDefault(),
  onCut: (e: React.ClipboardEvent) => e.preventDefault(),
  onChange: (e: React.ChangeEvent) => e.preventDefault(),
  onFocus: (e: React.FocusEvent<HTMLInputElement>) => e.target.blur(), // ✅ prevents format mask from showing
  style: {
    caretColor: "transparent",
    userSelect: "none",
    WebkitUserSelect: "none",
    MozUserSelect: "none",
    cursor: "pointer",
  },
} as any;

const blockedInputSx = {
  "& .MuiInputBase-root": {
    cursor: "pointer !important", //  changed from "default" to "pointer"
  },
  "& .MuiInputBase-input": {
    // ❌ REMOVED: pointerEvents: "none !important" — was blocking click/touch
    caretColor: "transparent !important",
    userSelect: "none !important",
    WebkitUserSelect: "none !important",
    MozUserSelect: "none !important",
    cursor: "pointer !important", //  changed from default
  },
  "& .MuiIconButton-root": {
    pointerEvents: "auto !important",
    cursor: "pointer !important",
  },
};
type timeFrameProps = {
  value: [any, any];
  onChange: (value: [any, any]) => void;
  label?: string;
  placeholder?: string;
  isRequired?: boolean;
  dataTracker?: string;
  isDisabled?: boolean;
  maxDate?: Dayjs;
  maxDays?: number;
  isHighlighted?: boolean;
};

const TimeFrameDateRange = forwardRef<HTMLDivElement, timeFrameProps>(
  (
    {
      value,
      onChange,
      label = "Time Period",
      placeholder = "Select Time Period",
      dataTracker,
      isDisabled,
      maxDate,
      maxDays,
      isHighlighted,
    },
    ref,
  ) => {
    const [tempValue, setTempValue] = useState<[any, any]>(value);
    const [open, setOpen] = useState(false);

    // When start date is selected and maxDays is set, cap the end date in the calendar
    const effectiveMaxDate = React.useMemo<Dayjs | undefined>(() => {
      if (maxDays && tempValue?.[0]) {
        const computed = dayjs(tempValue[0]).add(maxDays - 1, "day");
        return maxDate && maxDate.isBefore(computed) ? maxDate : computed;
      }
      return maxDate;
    }, [maxDays, tempValue, maxDate]);
    const tempValueRef = React.useRef<[any, any]>(value);
    const localRef = React.useRef<HTMLDivElement | null>(null);

    // Scroll this component into view whenever the highlight is true (when click on the Metric column value)
    useEffect(() => {
      if (isHighlighted && localRef.current) {
        localRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }, [isHighlighted]);

    useEffect(() => {
      setTempValue(value);
      tempValueRef.current = value;
    }, [value]);

    const hasCommittedRange = !!(value?.[0] && value?.[1]);
    const hasTempRange = !!(tempValue?.[0] && tempValue?.[1]);
    const showClearIcon = hasCommittedRange || hasTempRange;

    // When opening with no committed range, pre-select MTD in the picker
    const handleOpen = () => {
      // if (!hasCommittedRange) {
      //   const [mtdStart, mtdEnd] = getMTDRange();
      //   setTempValue([mtdStart, mtdEnd]);
      //   tempValueRef.current = [mtdStart, mtdEnd];
      // }
      setOpen(true);
    };

    const handleClear = (e: React.MouseEvent) => {
      e.stopPropagation();
      setTempValue([null, null]);
      tempValueRef.current = [null, null];
      onChange([null, null]);
      setOpen(false); // close picker if open
    };

    const handleClose = () => {
      setOpen(false);
      const [start, end] = tempValueRef.current || [];

      if (start && end) {
        const prevStart = value?.[0];
        const prevEnd = value?.[1];
        const isSame =
          prevStart &&
          prevEnd &&
          dayjs(start).isSame(dayjs(prevStart), "day") &&
          dayjs(end).isSame(dayjs(prevEnd), "day");

        if (!isSame) {
          onChange([start, end]);
        }
      } else {
        setTempValue(value);
        tempValueRef.current = value;
      }
    };

    const mergedSx: SxProps<Theme> = {
      ...blockedInputSx,
      ...(isHighlighted && {
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: "#d32f2f !important",
          borderWidth: "2px !important",
        },
        "& .MuiInputLabel-root": {
          color: "#d32f2f !important",
        },
      }),
    };

    return (
      <div
        ref={(el) => {
          localRef.current = el;
          if (typeof ref === "function") ref(el);
          else if (ref) (ref as any).current = el;
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DateRangePicker
            disabled={isDisabled}
            open={open}
            onOpen={handleOpen} //replaced setOpen(true) with handleOpen
            onClose={handleClose}
            slots={{ field: SingleInputDateRangeField }}
            value={tempValue}
            onChange={(newValue) => {
              setTempValue(newValue as [any, any]);
              tempValueRef.current = newValue as [any, any];
            }}
            maxDate={effectiveMaxDate}
            format="DD/MM/YYYY"
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true,
                //required: isRequired,
                color: "secondary",
                inputProps: blockedInputProps,
                sx: mergedSx,
                placeholder,
                InputLabelProps: { shrink: true },
                label,
                InputProps: {
                  endAdornment: (
                    <InputAdornment position="end" sx={{ gap: "2px" }}>
                      {showClearIcon && (
                        <IconButton
                          size="small"
                          onClick={handleClear}
                          disabled={isDisabled}
                          sx={{
                            padding: "2px",
                            color: isDisabled
                              ? "text.disabled"
                              : "text.secondary",
                            cursor: isDisabled ? "default" : "pointer",
                            pointerEvents: isDisabled ? "none" : "auto",
                            "&:hover": {
                              color: isDisabled
                                ? "text.disabled"
                                : "secondary.main",
                            },
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      )}
                      <CalendarMonthIcon
                        fontSize="medium"
                        onClick={() => !isDisabled && handleOpen()} // use handleOpen
                        sx={{
                          cursor: isDisabled ? "default" : "pointer",
                          pointerEvents: isDisabled ? "none" : "auto",
                          "&:hover": { color: "secondary.main" },
                        }}
                      />
                    </InputAdornment>
                  ),
                },
              },
              popper: {
                ...({
                  "data-tracker": dataTracker,
                } as React.HTMLAttributes<HTMLDivElement>),
              },
            }}
          />
        </LocalizationProvider>
      </div>
    );
  },
);

TimeFrameDateRange.displayName = "TimeFrameDateRange";
export default TimeFrameDateRange;
